import { useState, useEffect, useRef } from 'react';

function CountUp({ to, suffix = '', prefix = '' }: { to: number; suffix?: string; prefix?: string }) {
  const [n, setN] = useState(0);
  const [on, setOn] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !on) {
        setOn(true);
        const t0 = Date.now(); const dur = 2200;
        const tick = () => {
          const p = Math.min((Date.now() - t0) / dur, 1);
          setN(Math.floor((1 - Math.pow(1 - p, 3)) * to));
          if (p < 1) requestAnimationFrame(tick);
        };
        tick();
      }
    }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [to, on]);
  return <div ref={ref}>{prefix}{n.toLocaleString()}{suffix}</div>;
}

const PLATFORMS = [
  { icon: 'ri-google-line', name: 'Google Ads' },
  { icon: 'ri-meta-line', name: 'Meta Ads' },
  { icon: 'ri-search-line', name: 'Naver' },
  { icon: 'ri-instagram-line', name: 'Instagram' },
  { icon: 'ri-youtube-line', name: 'YouTube' },
  { icon: 'ri-kakao-talk-fill', name: 'Kakao' },
  { icon: 'ri-tiktok-line', name: 'TikTok' },
  { icon: 'ri-store-line', name: 'Coupang' },
];

const CASES = [
  {
    category: '패션 이커머스',
    period: '4개월',
    before: '4.8억',
    after: '20.1억',
    growth: '+318%',
    metrics: [
      { label: 'ROAS', before: '2.1배', after: '15.6배' },
      { label: '전환율', before: '0.8%', after: '3.5%' },
      { label: 'CPA', before: '18,000원', after: '6,300원' },
    ],
  },
  {
    category: '뷰티 D2C',
    period: '3개월',
    before: '1.2억',
    after: '4.8억',
    growth: '+300%',
    metrics: [
      { label: 'ROAS', before: '1.8배', after: '9.2배' },
      { label: '신규 고객', before: '月 340명', after: '月 2,100명' },
      { label: 'CPA', before: '22,000원', after: '8,800원' },
    ],
  },
  {
    category: '건강기능식품',
    period: '6개월',
    before: '0',
    after: '월 2억+',
    growth: '신규 채널',
    metrics: [
      { label: 'Google ROAS', before: '없음', after: '11.4배' },
      { label: '구독 전환', before: '0%', after: '28%' },
      { label: '재구매율', before: '12%', after: '61%' },
    ],
  },
];

export default function ResultsSection() {
  return (
    <section id="results" className="bg-[#050810] overflow-hidden">

      {/* Platform bar — auto marquee */}
      <div className="border-y border-white/5 py-3.5 md:py-5">
        <style>{`
          @keyframes ticker-scroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .ticker-track {
            animation: ticker-scroll 22s linear infinite;
            will-change: transform;
          }
          .ticker-track:hover {
            animation-play-state: paused;
          }
        `}</style>

        {/* Label row */}
        <div className="flex items-center justify-between max-w-7xl mx-auto px-5 md:px-8 mb-3">
          <span className="text-white/20 text-xs font-bold tracking-widest uppercase leading-relaxed">공식 인증 파트너</span>
          <div className="flex items-center gap-1.5 text-[#00FF87] text-xs leading-relaxed">
            <span className="w-1.5 h-1.5 bg-[#00FF87] rounded-full animate-pulse inline-block"></span>
            15개+ 플랫폼 공식 인증
          </div>
        </div>

        {/* Ticker */}
        <div className="relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-12 md:w-20 bg-gradient-to-r from-[#050810] to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-12 md:w-20 bg-gradient-to-l from-[#050810] to-transparent z-10 pointer-events-none" />
          <div className="ticker-track flex items-center gap-0">
            {[...PLATFORMS, ...PLATFORMS].map((p, i) => (
              <div
                key={i}
                className="flex items-center gap-2 md:gap-2.5 text-white/30 hover:text-white/70 transition-colors whitespace-nowrap px-5 md:px-8 cursor-default"
              >
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className={`${p.icon} text-sm md:text-base`}></i>
                </div>
                <span className="text-xs md:text-sm font-semibold">{p.name}</span>
                <span className="ml-4 md:ml-6 w-px h-3 bg-white/10 inline-block"></span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Big numbers */}
      <div className="py-10 md:py-24 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <p className="text-center text-white/20 text-xs font-bold tracking-widest uppercase mb-3 md:mb-4 leading-relaxed">누적 성과 데이터</p>
          <h2 className="text-center font-black text-white mb-8 md:mb-16 leading-tight"
            style={{ fontSize: 'clamp(1.5rem, 5vw, 3.5rem)', fontFamily: "'Space Grotesk', 'Noto Sans KR', sans-serif" }}>
            숫자로 증명합니다
          </h2>

          {/* 3-col grid on all screens */}
          <div className="grid grid-cols-3 divide-x divide-white/7 border border-white/7 rounded-2xl md:rounded-3xl overflow-hidden">
            {[
              { prefix: '+', to: 620, suffix: '%', label: '평균 매출 성장', note: '6개월 기준' },
              { prefix: '', to: 12, suffix: '배', label: '평균 ROAS', note: '광고비 대비' },
              { prefix: '', to: 350, suffix: '+', label: '성공 브랜드', note: '누적 관리' },
            ].map((m, i) => (
              <div key={i} className="text-center py-8 md:py-16 px-2 md:px-8 bg-[#0A0D1E] hover:bg-[#00FF87]/4 transition-colors duration-500 group flex flex-col items-center justify-center gap-1.5 md:gap-0">
                <div
                  className="font-black leading-none group-hover:scale-105 transition-transform duration-300 w-full text-center"
                  style={{
                    fontSize: 'clamp(1.4rem, 5.5vw, 5.5rem)',
                    fontFamily: "'Space Grotesk', sans-serif",
                    color: '#00FF87',
                    textShadow: '0 0 80px rgba(0,255,135,0.45)',
                    wordBreak: 'keep-all',
                  }}>
                  <CountUp to={m.to} prefix={m.prefix} suffix={m.suffix} />
                </div>
                <div className="text-white font-bold text-xs md:text-lg w-full text-center leading-snug break-words px-1">{m.label}</div>
                <div className="text-white/30 text-xs hidden md:block">{m.note}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Case studies */}
      <div className="py-10 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <p className="text-center text-white/20 text-xs font-bold tracking-widest uppercase mb-3 md:mb-4 leading-relaxed">실제 성과 사례</p>
          <h2 className="text-center font-black text-white mb-8 md:mb-16 leading-tight"
            style={{ fontSize: 'clamp(1.5rem, 5vw, 3.5rem)', fontFamily: "'Space Grotesk', 'Noto Sans KR', sans-serif" }}>
            Before → After
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mb-10 md:mb-12">
            {CASES.map((c, i) => (
              <div key={i} className="bg-[#0A0D1E] border border-white/7 rounded-2xl hover:border-[#00FF87]/20 transition-all duration-300">
                {/* Category header */}
                <div className="px-5 md:px-6 pt-5 md:pt-6 pb-4 border-b border-white/5">
                  <span className="text-[#00FF87]/60 text-xs font-bold tracking-widest uppercase leading-relaxed">{c.category}</span>
                  <div className="mt-3 flex items-end justify-between gap-2">
                    <div>
                      <div className="text-white/25 text-xs mb-1.5 leading-relaxed">시작 → {c.period} 후</div>
                      <div className="flex items-baseline gap-1.5 md:gap-2 flex-wrap">
                        <span className="text-white/30 font-bold text-lg md:text-xl line-through leading-none">{c.before}</span>
                        <i className="ri-arrow-right-line text-[#00FF87]/50 text-sm flex-shrink-0"></i>
                        <span className="font-black text-[#00FF87]"
                          style={{ fontSize: 'clamp(1.3rem, 4vw, 1.8rem)', fontFamily: "'Space Grotesk', sans-serif", textShadow: '0 0 30px rgba(0,255,135,0.5)', lineHeight: 1 }}>
                          {c.after}
                        </span>
                      </div>
                    </div>
                    <span className="text-xs font-black bg-[#00FF87]/15 text-[#00FF87] border border-[#00FF87]/20 px-2.5 py-1 rounded-full flex-shrink-0 leading-relaxed">
                      {c.growth}
                    </span>
                  </div>
                </div>
                {/* Metrics */}
                <div className="p-5 md:p-6 space-y-3.5 md:space-y-3">
                  {c.metrics.map((m, mi) => (
                    <div key={mi} className="flex items-center justify-between gap-2">
                      <span className="text-white/35 text-xs leading-relaxed">{m.label}</span>
                      <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
                        <span className="text-white/25 text-xs line-through">{m.before}</span>
                        <i className="ri-arrow-right-line text-white/15 text-xs"></i>
                        <span className="text-[#00FF87] text-xs md:text-sm font-bold">{m.after}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center">
            <button
              className="inline-flex items-center gap-2 md:gap-3 bg-[#00FF87] hover:bg-[#00e87a] text-black font-black px-8 md:px-14 py-4 md:py-5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap text-base md:text-lg w-full sm:w-auto justify-center"
              style={{ boxShadow: '0 0 50px rgba(0,255,135,0.3)' }}
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              광고 진단 무료로 받기
              <i className="ri-arrow-right-line text-lg md:text-xl"></i>
            </button>
            <p className="text-white/20 text-xs md:text-sm mt-3.5 leading-relaxed">
              상담 비용 0원 · 계약 강요 없음<br className="block md:hidden" />· 24H 응답
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
